import { useState, useCallback, useEffect } from 'react';
import { FREEMIUM_ENABLED, PREMIUM_KEY, FREE_LIMITS } from '../config/freemiumConfig';
import { checkPremiumStatus } from '../services/revenuecat';
import { isNative } from '../config/apiConfig';

export function useFreemium() {
  const [isPremium, setIsPremium] = useState(() => {
    if (!FREEMIUM_ENABLED) return true;
    try { return localStorage.getItem(PREMIUM_KEY) === 'true'; } catch { return false; }
  });

  // Bei App-Start echten Status von RevenueCat holen
  useEffect(() => {
    if (!FREEMIUM_ENABLED || !isNative()) return;
    checkPremiumStatus().then(status => {
      if (status !== isPremium) {
        localStorage.setItem(PREMIUM_KEY, String(status));
        setIsPremium(status);
      }
    });
  }, []); // eslint-disable-line

  // Sync across all useFreemium instances in the same tab
  useEffect(() => {
    const onPremiumChange = (e) => setIsPremium(e.detail.premium);
    window.addEventListener('bullivo:premiumChanged', onPremiumChange);
    return () => window.removeEventListener('bullivo:premiumChanged', onPremiumChange);
  }, []);

  const activatePremium = useCallback(() => {
    localStorage.setItem(PREMIUM_KEY, 'true');
    setIsPremium(true);
    window.dispatchEvent(new CustomEvent('bullivo:premiumChanged', { detail: { premium: true } }));
  }, []);

  const deactivatePremium = useCallback(() => {
    localStorage.removeItem(PREMIUM_KEY);
    setIsPremium(false);
    window.dispatchEvent(new CustomEvent('bullivo:premiumChanged', { detail: { premium: false } }));
  }, []);

  const canAccess = useCallback((feature) => {
    if (!FREEMIUM_ENABLED || isPremium) return true;
    switch (feature) {
      case 'alerts':       return FREE_LIMITS.alertsEnabled;
      default:             return true;
    }
  }, [isPremium]);

  const getLimit = useCallback((feature) => {
    if (!FREEMIUM_ENABLED || isPremium) return null;
    switch (feature) {
      case 'homeSignals':     return FREE_LIMITS.homeSignals;
      case 'discoverSignals': return FREE_LIMITS.discoverSignals;
      case 'analyseTrends':   return FREE_LIMITS.analyseTrends;
      default:                return null;
    }
  }, [isPremium]);

  return {
    isPremium,
    freemiumActive: FREEMIUM_ENABLED,
    activatePremium,
    deactivatePremium,
    canAccess,
    getLimit,
  };
}
